import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from './Card';
import { Suit, Rank, Card as CardT } from '../../types/Card';
import './Tutorial.css';

interface TutorialProps {
  open: boolean;
  onClose: () => void;
}

const mkCard = (suit: Suit, rank: Rank): CardT => ({
  suit, rank, id: `${suit}-${rank}`, isFaceUp: true,
});

const SUIT_SYMBOLS: Record<string, string> = {
  spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣',
};
const SUIT_NAMES: Record<string, string> = {
  spades: 'Spades', hearts: 'Hearts', diamonds: 'Diamonds', clubs: 'Clubs',
};

interface StepDef {
  title: string;
  description: string;
  hand: CardT[];
  centerPile: CardT[];
  leadSuit: Suit | null;
  validIds: string[];
  prompt: string;
  hint?: string;
  getResult: (playedCard: CardT | null) => {
    centerAfter: CardT[];
    message: string;
    outcome: string;
  };
  isReadOnly?: boolean;
  readOnlyResult?: string;
  showBorrowDemo?: boolean;
  showResignDemo?: boolean;
  showControlsDemo?: boolean;
}

const STEPS: StepDef[] = [
  {
    title: 'Starting the Game',
    description: 'All 52 cards are dealt. The player with the Ace of Spades plays first. They can play any card to set the lead suit for the round.',
    hand: [
      mkCard(Suit.SPADES, Rank.ACE),
      mkCard(Suit.SPADES, Rank.SEVEN),
      mkCard(Suit.HEARTS, Rank.KING),
      mkCard(Suit.DIAMONDS, Rank.FIVE),
      mkCard(Suit.CLUBS, Rank.TEN),
    ],
    centerPile: [],
    leadSuit: null,
    validIds: ['spades-A', 'spades-7', 'hearts-K', 'diamonds-5', 'clubs-10'],
    prompt: 'Tap any card to play it',
    hint: 'No lead suit yet — all cards are valid!',
    getResult: (card) => {
      if (!card) return { centerAfter: [], message: '', outcome: '' };
      return {
        centerAfter: [card],
        message: `You played ${card.rank}${SUIT_SYMBOLS[card.suit]}`,
        outcome: `Lead suit is now ${SUIT_NAMES[card.suit]} ${SUIT_SYMBOLS[card.suit]}. Other players must follow this suit.`,
      };
    },
  },
  {
    title: 'Following Suit',
    description: 'Another player led with 7♠. The lead suit is Spades. Since you have Spades in your hand, you must play one.',
    hand: [
      mkCard(Suit.SPADES, Rank.QUEEN),
      mkCard(Suit.SPADES, Rank.THREE),
      mkCard(Suit.HEARTS, Rank.KING),
      mkCard(Suit.DIAMONDS, Rank.FIVE),
    ],
    centerPile: [mkCard(Suit.SPADES, Rank.SEVEN)],
    leadSuit: Suit.SPADES,
    validIds: ['spades-Q', 'spades-3'],
    prompt: 'Tap a Spade to follow suit',
    hint: 'Hearts and Diamonds are dimmed — only Spades are playable.',
    getResult: (card) => {
      if (!card) return { centerAfter: [], message: '', outcome: '' };
      const isQueen = card.rank === Rank.QUEEN;
      return {
        centerAfter: [mkCard(Suit.SPADES, Rank.SEVEN), card],
        message: `You played ${card.rank}${SUIT_SYMBOLS[card.suit]}`,
        outcome: isQueen
          ? 'Q♠ beats 7♠ — you win the trick! All cards are discarded. You lead the next round.'
          : '3♠ is lower than 7♠ — the other player wins this trick and leads next.',
      };
    },
  },
  {
    title: 'The Cut!',
    description: 'The lead suit is Hearts, but you have no Hearts. Playing any card causes a "Cut" — the trick ends immediately.',
    hand: [
      mkCard(Suit.SPADES, Rank.FOUR),
      mkCard(Suit.DIAMONDS, Rank.JACK),
      mkCard(Suit.CLUBS, Rank.NINE),
      mkCard(Suit.CLUBS, Rank.TWO),
    ],
    centerPile: [mkCard(Suit.HEARTS, Rank.TEN), mkCard(Suit.HEARTS, Rank.SIX)],
    leadSuit: Suit.HEARTS,
    validIds: ['spades-4', 'diamonds-J', 'clubs-9', 'clubs-2'],
    prompt: 'Tap any card — it will cause a Cut!',
    hint: 'No Hearts in your hand. Any card you play breaks the suit.',
    getResult: (card) => {
      if (!card) return { centerAfter: [], message: '', outcome: '' };
      return {
        centerAfter: [mkCard(Suit.HEARTS, Rank.TEN), mkCard(Suit.HEARTS, Rank.SIX), card],
        message: `CUT! You played ${card.rank}${SUIT_SYMBOLS[card.suit]}`,
        outcome: 'The player with 10♥ (highest Heart) must pick up ALL 3 cards. They start the next round.',
      };
    },
  },
  {
    title: 'Borrowing Cards',
    description: 'Between rounds (empty center pile), you can request to take all cards from a neighbor. If they accept, they become Safe immediately.',
    hand: [
      mkCard(Suit.HEARTS, Rank.ACE),
      mkCard(Suit.HEARTS, Rank.KING),
      mkCard(Suit.SPADES, Rank.TWO),
      mkCard(Suit.DIAMONDS, Rank.THREE),
      mkCard(Suit.CLUBS, Rank.FOUR),
    ],
    centerPile: [],
    leadSuit: null,
    validIds: [],
    prompt: '',
    isReadOnly: true,
    showBorrowDemo: true,
    readOnlyResult: 'In a real game, click the 🤝 button on a neighbor\'s badge to request their cards. They can accept (becoming Safe) or reject. Use this when you have strong cards and want more to play with!',
    getResult: () => ({ centerAfter: [], message: '', outcome: '' }),
  },
  {
    title: 'Resigning',
    description: 'When only 2 players remain, you can choose to resign if the position feels hopeless — for example, when you and your opponent have completely different suits, leading to endless cuts.',
    hand: [],
    centerPile: [],
    leadSuit: null,
    validIds: [],
    prompt: '',
    isReadOnly: true,
    showResignDemo: true,
    readOnlyResult: 'The 🚩 resign button appears once the game is down to 2 players. Resigning makes you the loser and ends the game immediately. Use it when you\'re stuck in an unwinnable stalemate!',
    getResult: () => ({ centerAfter: [], message: '', outcome: '' }),
  },
  {
    title: 'Game Controls',
    description: 'During gameplay, control buttons appear in the top-left corner. Here\'s what each one does:',
    hand: [],
    centerPile: [],
    leadSuit: null,
    validIds: [],
    prompt: '',
    isReadOnly: true,
    showControlsDemo: true,
    readOnlyResult: '',
    getResult: () => ({ centerAfter: [], message: '', outcome: '' }),
  },
  {
    title: 'Winning & Losing',
    description: 'Empty your hand to become Safe! The last player holding cards is the Loser. Play your final card wisely.',
    hand: [
      mkCard(Suit.SPADES, Rank.ACE),
    ],
    centerPile: [],
    leadSuit: null,
    validIds: ['spades-A'],
    prompt: 'Play your last card to become Safe!',
    hint: 'One card left — this is your moment!',
    getResult: (card) => {
      if (!card) return { centerAfter: [], message: '', outcome: '' };
      return {
        centerAfter: [card],
        message: `You played your last card — ${card.rank}${SUIT_SYMBOLS[card.suit]}`,
        outcome: 'You\'re Safe! 🎉 You have no more cards and exit the game. The remaining players continue until one is left — that player loses.',
      };
    },
  },
];

const Tutorial: React.FC<TutorialProps> = ({ open, onClose }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [playedCard, setPlayedCard] = useState<CardT | null>(null);
  const titleId = React.useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;
  const hasPlayed = playedCard !== null;
  const result = hasPlayed ? step.getResult(playedCard) : null;

  const goTo = useCallback((idx: number) => {
    setStepIdx(idx);
    setPlayedCard(null);
  }, []);

  const handleNext = () => {
    if (isLast) { goTo(0); onClose(); return; }
    goTo(stepIdx + 1);
  };

  const handlePrev = () => goTo(Math.max(0, stepIdx - 1));

  const handleCardClick = (card: CardT) => {
    if (hasPlayed) return;
    if (!step.validIds.includes(card.id)) return;
    setPlayedCard(card);
  };

  const handleClose = () => { goTo(0); onClose(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="tutorial-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleClose}
        >
          <motion.div
            className="tutorial-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="tutorial-header">
              <div className="tutorial-step-indicator" aria-hidden="true">
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className={`step-dot ${i === stepIdx ? 'active' : ''} ${i < stepIdx ? 'done' : ''}`}
                  />
                ))}
              </div>
              <button className="tutorial-close" onClick={handleClose} aria-label="Close tutorial">✕</button>
            </div>

            {/* Content */}
            <div className="tutorial-content">
              <h3 id={titleId} className="tutorial-title">
                <span className="tutorial-step-num">Step {stepIdx + 1}</span>
                {step.title}
              </h3>
              <p className="tutorial-desc">{step.description}</p>

              {/* Lead suit indicator */}
              {step.leadSuit && !hasPlayed && (
                <div className="tutorial-lead">
                  Lead Suit: <span className={step.leadSuit === 'hearts' || step.leadSuit === 'diamonds' ? 'red' : ''}>
                    {SUIT_SYMBOLS[step.leadSuit]} {SUIT_NAMES[step.leadSuit]}
                  </span>
                </div>
              )}

              {/* Center pile — before or after playing */}
              <div className="tutorial-table">
                <div className="tutorial-center">
                  {(hasPlayed && result ? result.centerAfter : step.centerPile).map((card, i) => (
                    <motion.div
                      key={`${card.id}-${i}`}
                      className="tutorial-center-card"
                      initial={hasPlayed && i === (result?.centerAfter.length ?? 1) - 1 ? { scale: 0, y: 30 } : false}
                      animate={{ scale: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      <Card card={{ ...card, isFaceUp: true }} />
                    </motion.div>
                  ))}
                  {step.centerPile.length === 0 && !hasPlayed && !step.isReadOnly && (
                    <span className="tutorial-center-empty">Center Pile (empty)</span>
                  )}
                </div>
              </div>

              {/* Result panel */}
              {hasPlayed && result && (
                <motion.div
                  className="tutorial-result"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="tutorial-result-title">{result.message}</div>
                  <div className="tutorial-result-outcome">{result.outcome}</div>
                </motion.div>
              )}

              {/* Read-only result for step 4 */}
              {step.isReadOnly && step.readOnlyResult && (
                <div className="tutorial-result read-only">
                  <div className="tutorial-result-outcome">{step.readOnlyResult}</div>
                </div>
              )}

              {/* Your hand */}
              {!step.isReadOnly && (
                <>
                  <div className="tutorial-hand-label">
                    {hasPlayed ? 'Your Hand (after playing)' : 'Your Hand'}
                  </div>
                  <div className="tutorial-hand">
                    {step.hand.map((card) => {
                      const isValid = step.validIds.includes(card.id);
                      const isDimmed = step.validIds.length > 0 && !isValid && step.leadSuit !== null;
                      const wasPlayed = hasPlayed && playedCard?.id === card.id;
                      return (
                        <AnimatePresence key={card.id}>
                          {!wasPlayed && (
                            <motion.div
                              className={`tutorial-card ${isValid && !hasPlayed ? 'valid' : ''} ${isDimmed && !hasPlayed ? 'dimmed' : ''}`}
                              onClick={() => handleCardClick(card)}
                              whileHover={isValid && !hasPlayed ? { y: -10, scale: 1.06 } : {}}
                              whileTap={isValid && !hasPlayed ? { scale: 0.95 } : {}}
                              exit={{ scale: 0, opacity: 0, y: -20 }}
                              transition={{ duration: 0.3 }}
                              layout
                            >
                              <Card
                                card={{ ...card, isFaceUp: true }}
                                isPlayable={isValid && !hasPlayed}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Borrow step — show dummy players */}
              {step.showBorrowDemo && (
                <div className="tutorial-borrow-demo">
                  <div className="tutorial-borrow-player">
                    <span className="tutorial-borrow-avatar" style={{ background: '#7c3aed' }}>🧙</span>
                    <span>You (5 cards)</span>
                  </div>
                  <div className="tutorial-borrow-arrow">🤝 Request</div>
                  <div className="tutorial-borrow-player">
                    <span className="tutorial-borrow-avatar" style={{ background: '#b45309' }}>🤠</span>
                    <span>Neighbor (3 cards)</span>
                  </div>
                  <div className="tutorial-borrow-result">
                    If accepted → You get 8 cards, Neighbor becomes Safe
                  </div>
                </div>
              )}

              {/* Resign step — show resign demo */}
              {step.showResignDemo && (
                <div className="tutorial-borrow-demo">
                  <div className="tutorial-borrow-player">
                    <span className="tutorial-borrow-avatar" style={{ background: '#7c3aed' }}>🧙</span>
                    <span>You (12 cards: ♠♣)</span>
                  </div>
                  <div className="tutorial-borrow-arrow" style={{ color: '#ef4444' }}>🚩 Resign</div>
                  <div className="tutorial-borrow-player">
                    <span className="tutorial-borrow-avatar" style={{ background: '#b45309' }}>🤠</span>
                    <span>Opponent (4 cards: ♥♦)</span>
                  </div>
                  <div className="tutorial-borrow-result">
                    No suit overlap → every round is a cut → stalemate. Resign to end the game.
                  </div>
                </div>
              )}

              {/* Controls step — show control descriptions */}
              {step.showControlsDemo && (
                <div className="tutorial-controls-demo">
                  <div className="tutorial-control-row">
                    <span className="tutorial-control-icon">?</span>
                    <div className="tutorial-control-info">
                      <span className="tutorial-control-name">Rules</span>
                      <span className="tutorial-control-desc">View the game rules anytime</span>
                    </div>
                  </div>
                  <div className="tutorial-control-row">
                    <span className="tutorial-control-icon">🔊</span>
                    <div className="tutorial-control-info">
                      <span className="tutorial-control-name">Sound</span>
                      <span className="tutorial-control-desc">Toggle sound effects on/off. Use the slider to adjust volume</span>
                    </div>
                  </div>
                  <div className="tutorial-control-row">
                    <span className="tutorial-control-icon">⇅ Suit</span>
                    <div className="tutorial-control-info">
                      <span className="tutorial-control-name">Sort Cards</span>
                      <span className="tutorial-control-desc">Cycle between sorting by Suit, Rank, or Off</span>
                    </div>
                  </div>
                  <div className="tutorial-control-row">
                    <span className="tutorial-control-icon tutorial-control-confirm">✓ Off</span>
                    <div className="tutorial-control-info">
                      <span className="tutorial-control-name">Confirm Mode</span>
                      <span className="tutorial-control-desc">When ON, tap a card to select it, then confirm to play. Prevents accidental card plays on mobile!</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action prompt */}
              {!hasPlayed && !step.isReadOnly && (
                <div className="tutorial-action">
                  {step.prompt}
                </div>
              )}
              {!hasPlayed && step.hint && !step.isReadOnly && (
                <div className="tutorial-hint">{step.hint}</div>
              )}
            </div>

            {/* Navigation */}
            <div className="tutorial-nav">
              <button
                className="tutorial-nav-btn"
                onClick={handlePrev}
                disabled={stepIdx === 0}
              >
                ← Prev
              </button>
              <span className="tutorial-step-count">{stepIdx + 1} / {STEPS.length}</span>
              <button
                className="tutorial-nav-btn primary"
                onClick={handleNext}
              >
                {isLast ? 'Finish' : 'Next →'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Tutorial;
